import axios, { AxiosError } from 'axios'
import { toast } from 'sonner'
import { setThrottling } from './reqConfig'

// 创建 axios 实例
const axiosInstance = axios.create({
  timeout: 30000,
})

const axiosNoThrottleInstance = axios.create({
  timeout: 30000,
})

// 应用节流
setThrottling(axiosInstance, 1000)

// 获取 base URL（根据 master 模式）
const getBaseUrl = () => {
  const isMaster = localStorage.getItem('master') === 'master'
  const baseUrl = import.meta.env.VITE_ALANT_URL || ''
  const proxyUrl = import.meta.env.VITE_PROXY_URL || ''
  return isMaster ? proxyUrl : baseUrl
}

// 响应数据接口
interface RequestResponse<T = any> {
  code: number
  data: T
  msg?: string
}

// ============= 管理后台请求函数 =============

/**
 * 管理后台通用请求
 * @param path 接口路径
 * @param data 请求数据
 * @param skipAuth 是否跳过 Authorization 头（登录接口使用）
 */
export const request = async <T = any>(
  path: string,
  data?: any,
  skipAuth: boolean = false
): Promise<RequestResponse<T>> => {
  try {
    const url = getBaseUrl() + path
    const token = localStorage.getItem('token')

    // 直接在请求中设置 headers（模仿原项目）
    const response = await axiosInstance.post(url, data, {
      headers: skipAuth || !token ? {} : {
        Authorization: token
      }
    })

    // 检查认证失败
    if (response.data.code && response.data.code > 4000) {
      localStorage.removeItem('token')
      localStorage.removeItem('master')
      // 使用 href 确保在 PWA 模式下正确跳转
      window.location.href = '/admin/login'
      return { code: -1, data: {} as T }
    }

    return response.data
  } catch (error) {
    console.error('Request error:', error)
    return {
      code: -1,
      data: {} as T,
      msg: '请求失败'
    }
  }
}

/**
 * 管理后台 Master 请求（固定使用 ALANT_URL）
 * @param path 接口路径
 * @param data 请求数据
 */
export const requestMaster = async <T = any>(
  path: string,
  data?: any
): Promise<RequestResponse<T>> => {
  try {
    const baseUrl = import.meta.env.VITE_ALANT_URL || ''
    const url = baseUrl + path
    const token = localStorage.getItem('token')

    const response = await axiosInstance.post(url, data, {
      headers: token ? {
        Authorization: token
      } : {}
    })

    if (response.data.code && response.data.code > 4000) {
      localStorage.removeItem('token')
      localStorage.removeItem('master')
      // 使用 href 确保在 PWA 模式下正确跳转
      window.location.href = '/admin/login'
    }

    return response.data
  } catch (error) {
    console.error('Request error:', error)
    return {
      code: -1,
      data: {} as T,
      msg: '请求失败'
    }
  }
}

/**
 * 管理后台代理请求（固定使用 VITE_PROXY_URL）
 * @param path 接口路径
 * @param data 请求数据
 */
export const requestProxy = async <T = unknown>(
  path: string,
  data?: unknown
): Promise<RequestResponse<T>> => {
  try {
    const baseUrl = import.meta.env.VITE_PROXY_URL || ''
    const url = baseUrl + path
    const token = localStorage.getItem('token')

    const response = await axiosInstance.post(url, data, {
      headers: token ? {
        Authorization: token
      } : {}
    })

    if (response.data.code && response.data.code > 4000) {
      localStorage.removeItem('token')
      localStorage.removeItem('master')
      window.location.href = '/admin/login'
      return { code: -1, data: {} as T }
    }

    return response.data
  } catch (error) {
    console.error('Request error:', error)
    return {
      code: -1,
      data: {} as T,
      msg: '请求失败'
    }
  }
}

/**
 * 管理后台代理请求（固定使用 VITE_PROXY_URL，不走前端节流）
 * 用于同一页面内部需要连续查询分页数据的场景。
 * @param path 接口路径
 * @param data 请求数据
 */
export const requestProxyNoThrottle = async <T = unknown>(
  path: string,
  data?: unknown
): Promise<RequestResponse<T>> => {
  try {
    const baseUrl = import.meta.env.VITE_PROXY_URL || ''
    const url = baseUrl + path
    const token = localStorage.getItem('token')

    const response = await axiosNoThrottleInstance.post(url, data, {
      headers: token ? {
        Authorization: token
      } : {}
    })

    if (response.data.code && response.data.code > 4000) {
      localStorage.removeItem('token')
      localStorage.removeItem('master')
      window.location.href = '/admin/login'
      return { code: -1, data: {} as T }
    }

    return response.data
  } catch (error) {
    console.error('Request error:', error)
    return {
      code: -1,
      data: {} as T,
      msg: '请求失败'
    }
  }
}

// ============= 移动端请求函数 =============

let requestCount = 0

/**
 * 移动端请求（带 loading 和 toast）
 * @param path 接口路径
 * @param data 请求数据
 * @param skipAuth 是否跳过 Authorization 头（登录接口使用）
 */
export const requestMobile = async <T = any>(
  path: string,
  data?: any,
  skipAuth: boolean = false
): Promise<RequestResponse<T> | null> => {
  requestCount++
  if (requestCount > 0) {
    toast.loading('加载中...', { duration: 0 })
  }

  try {
    const url = path.includes('http') ? path : getBaseUrl() + path
    const token = localStorage.getItem('token')

    // 直接在请求中设置 headers（模仿原项目）
    const response = await axiosInstance.post(url, data, {
      headers: skipAuth || !token ? {} : {
        Authorization: token
      }
    })

    requestCount--
    if (requestCount === 0) {
      toast.dismiss()
    }

    if (!response.data) {
      return { code: -1, data: {} as T }
    }

    if (response.data.code === 0) {
      return response.data
    } else if (response.data.code && response.data.code > 4000) {
      localStorage.removeItem('token')
      // 使用 href 确保在 PWA 模式下正确跳转
      window.location.href = '/mobile/login'
      return null
    } else {
      toast.error(response.data.msg || '请求失败', { duration: 5000 })
      return null
    }
  } catch (error) {
    requestCount--
    if (requestCount === 0) {
      toast.dismiss()
    }
    console.error('Request error:', error)
    return null
  }
}

export default request
